import type { HandledErrorJson } from "@common/consts/errors";
import { TRPCClientError } from "@trpc/client";
import type { AppRouter } from "@web/src/api/trpc-server";
import type { NextApiRequest, NextApiResponse } from "next";

export class APIError extends Error {
  httpStatus: number;
  messageOrErrorObj: string | HandledErrorJson;

  constructor(
    messageOrErrorObj: string | HandledErrorJson,
    httpStatus: number
  ) {
    super(
      typeof messageOrErrorObj === "string"
        ? messageOrErrorObj
        : messageOrErrorObj.message
    );
    this.messageOrErrorObj = messageOrErrorObj;
    this.httpStatus = httpStatus;
  }

  static fromHandledErrorJson(handledError: HandledErrorJson) {
    return new APIError(handledError, handledError.httpStatus);
  }
}

export function isTRPCClientError(
  error: unknown
): error is TRPCClientError<AppRouter> {
  return error instanceof TRPCClientError;
}

function requireFields(obj: any) {
  for (let key of Object.keys(obj)) {
    let val = obj[key];
    if (val === null || val === undefined) {
      throw new APIError(`Required field not present: ${key}`, 403);
    }
  }
}

function handleAPIError(error: unknown, res: NextApiResponse) {
  console.error(error instanceof Error ? error.stack : error);

  let httpStatus = 500;
  let responseData: Pick<APIResponse<unknown>, "error"> = {
    error: {
      message: "Unknown error occured",
    },
  };

  if (error instanceof APIError) {
    httpStatus = error.httpStatus;
    responseData.error =
      typeof error.messageOrErrorObj === "string"
        ? { message: error.messageOrErrorObj }
        : error.messageOrErrorObj;
  } else if (error instanceof Error) {
    responseData.error = {
      message: error.message,
    };
  }

  res.status(httpStatus).json(responseData);
}

function respondJson<T>(res: NextApiResponse, data: T, code = 200) {
  res.status(code).json({
    data,
  });
}

export type APIResponse<T> = {
  error?: { message: string } | HandledErrorJson;
  data: T;
};

export interface APIGetRequest<T extends { [key: string]: string[] | string }>
  extends NextApiRequest {
  method: "GET";
  query: T;
}
export interface APIPutRequest<
  T,
  K extends { [key: string]: string[] | string } = {}
> extends NextApiRequest {
  method: "PUT";
  body: T;
  query: K;
}

export interface APIPostRequest<
  TBody,
  TQuery extends { [key: string]: string[] | string } = {}
> extends NextApiRequest {
  method: "POST";
  body: TBody;
  query: TQuery;
}

export interface APIDeleteRequest<
  T,
  K extends { [key: string]: string[] | string } = {}
> extends NextApiRequest {
  method: "DELETE";
  body: T;
  query: K;
}

const APIUtils = {
  requireFields,
  handleAPIError,
  respondJson,
};

export default APIUtils;
